import {ValueType} from "../../common/input/types";
import _ from "lodash";
import {
  ConfigurationMetadataPart,
  ConfigurationPart,
  ViewSettings
} from "../../../store/configuration/types";
import {getSettingKey} from "../../../store/configuration/utils";

export const FORMS_CONFIGURATION_SOURCE_SETTING = "formsConfigurationSource";

export const FORMS_CONFIGURATION_SOURCE = {
  ADMIN_PORTAL: 0,
  X5: 1
} as const;

export enum FormsConfigurationSourceSwitchDirection {
  X5_TO_ADMIN_PORTAL = "X5_TO_ADMIN_PORTAL",
  ADMIN_PORTAL_TO_X5 = "ADMIN_PORTAL_TO_X5"
}

export enum FormsConfigurationSourceDialogAction {
  CANCEL = "CANCEL",
  SWITCH_TO_ADMIN_PORTAL = "SWITCH_TO_ADMIN_PORTAL",
  SWITCH_AND_IMPORT = "SWITCH_AND_IMPORT",
  SWITCH_WITHOUT_IMPORT = "SWITCH_WITHOUT_IMPORT"
}

export interface FormsConfigurationSourceWarningData {
  switchDirection: FormsConfigurationSourceSwitchDirection;
  originalValue: ValueType;
}

export const FORMS_CONFIGURATION_SOURCE_WARNING_MESSAGES = {
  [FormsConfigurationSourceSwitchDirection.X5_TO_ADMIN_PORTAL]:
    "You are changing the Form Configuration Source. The mobile application will stop using Form Configurations stored in X5 and will begin using configurations from the Admin Portal. No configurations will be imported or overwritten. Existing X5 configurations will be preserved but will not be used while the Admin Portal is selected as the active configuration source.",
  [FormsConfigurationSourceSwitchDirection.ADMIN_PORTAL_TO_X5]:
    "You are changing the Form Configuration Source. Importing configurations from the Admin Portal will overwrite the current Form Configurations stored in X5. Any existing X5 configuration changes may be lost. Please select one of the following options:"
};

export interface FormsConfigurationSourceWarningDialogAction {
  label: string;
  action: FormsConfigurationSourceDialogAction;
  variant?: "primary" | "secondary";
}

export interface FormsConfigurationSourceWarningDialogConfig {
  text: string;
  title: string;
  cancelText: string;
  actions: FormsConfigurationSourceWarningDialogAction[];
}

export function getFormsConfigurationSourceWarningDialogConfig(
  switchDirection: FormsConfigurationSourceSwitchDirection
): FormsConfigurationSourceWarningDialogConfig {
  const baseConfig = {
    title: "Warning",
    cancelText: "Cancel",
    text: FORMS_CONFIGURATION_SOURCE_WARNING_MESSAGES[switchDirection]
  };

  if (switchDirection === FormsConfigurationSourceSwitchDirection.X5_TO_ADMIN_PORTAL) {
    return {
      ...baseConfig,
      actions: [
        {
          label: "Switch to Admin Portal",
          action: FormsConfigurationSourceDialogAction.SWITCH_TO_ADMIN_PORTAL,
          variant: "primary"
        }
      ]
    };
  }

  return {
    ...baseConfig,
    actions: [
      {
        label: "Switch Without Importing",
        action: FormsConfigurationSourceDialogAction.SWITCH_WITHOUT_IMPORT,
        variant: "secondary"
      },
      {
        label: "Switch & Import Configurations",
        action: FormsConfigurationSourceDialogAction.SWITCH_AND_IMPORT,
        variant: "primary"
      }
    ]
  };
}

export function determineFormsConfigurationSourceSwitchDirection(
  currentSource: ValueType,
  newSource: ValueType
): FormsConfigurationSourceSwitchDirection | null {
  if (currentSource === newSource) {
    return null;
  }

  if (currentSource === FORMS_CONFIGURATION_SOURCE.X5 && newSource === FORMS_CONFIGURATION_SOURCE.ADMIN_PORTAL) {
    return FormsConfigurationSourceSwitchDirection.X5_TO_ADMIN_PORTAL;
  }

  if (currentSource === FORMS_CONFIGURATION_SOURCE.ADMIN_PORTAL && newSource === FORMS_CONFIGURATION_SOURCE.X5) {
    return FormsConfigurationSourceSwitchDirection.ADMIN_PORTAL_TO_X5;
  }

  return null;
}

export function getFormsConfigurationSourceWarning(
  configPart: ConfigurationPart,
  metadataPart: ConfigurationMetadataPart,
  viewSettings: ViewSettings
): FormsConfigurationSourceWarningData | null {
  const settingMD = _.find(metadataPart.settingsMetadata, {name: FORMS_CONFIGURATION_SOURCE_SETTING});
  if (!settingMD) {
    return null;
  }

  const viewSetting = viewSettings[getSettingKey(metadataPart, settingMD)];
  if (!viewSetting?.isDirty) {
    return null;
  }

  const savedSetting = _.find(configPart.settings, {name: FORMS_CONFIGURATION_SOURCE_SETTING});
  const originalValue = savedSetting?.value as ValueType;
  const switchDirection = determineFormsConfigurationSourceSwitchDirection(originalValue, viewSetting.value);

  if (!switchDirection) {
    return null;
  }

  return {switchDirection, originalValue};
}
